import { Injectable } from '@angular/core';
import { TransactionType } from './transaction-constants';
import { ObjectTypeCode, SystemTypeCode, EntityTypeCode } from './system-type-constants';
import { PriorAuthorizationRequestType } from './ref-constants';

@Injectable({
  providedIn: 'root',
})
export class AppUrls {
  static entities = {
    employer: {
      details(id: number): string {
        return `/employer/${id}`;
      },
    },
    insurer: {
      details(id: number): string {
        return `/insurer/${id}`;
      },
      create(): string {
        return '/insurer/create';
      },
    },
    healthcareProvider: {
      details(id: number): string {
        return `/healthcare-provider/${id}`;
      },
      create(): string {
        return '/healthcare-provider/create';
      },
    },
    medicalSupplier: {
      details(id: number): string {
        return `/medical-supplier/${id}`;
      },
      create(): string {
        return '/medical-supplier/create';
      },
    },
    claimant: {
      details(id: number): string {
        return `/claimant/${id}`;
      },
      create(): string {
        return '/claimant/create';
      },
    },
    thirdPartyAdministrator: {
      details(id: number): string {
        return `/third-party-administrator/${id}`;
      },
      create(): string {
        return '/third-party-administrator/create';
      },
    },
    medicalBillingDispute: {
      details(id: number): string {
        return `/medical-billing-dispute/${id}`;
      },
    },
    pharmacyBenefitManager: {
      details(): string {
        return null;
      },
    },
    utilizationReviewer: {
      details(): string {
        return null;
      },
    },
  };

  static transactions = {
    claim: {
      details(id: number): string {
        return `/claim/${id}`;
      },
      create(): string {
        return '/claim/create';
      },
    },
  };

  static document = {
    details(id: number): string {
      return `/document/${id}`;
    },
  };

  static formTemplate = {
    list: () => '/form-template-admin/templates',
    create: () => '/form-template-admin/template/create',
    details: (id: number) => `/form-template-admin/template/${id}`,
  };

  static medicalBillingDispute = {
    details: (id: number): string => `/medical-billing-dispute/${id}`,
  };

  static filings = {
    medicalBillingDispute: {
      create: (): string => '/webforms/medical-billing-dispute/create',
      edit: (id: number): string => `/webforms/medical-billing-dispute/${id}`,
    },
    priorAuthRequest: {
      create: () => '/webforms/prior-auth-request',
      editConfirmation: (filingId: number) =>
        `/webforms/prior-auth-request/confirmation/${filingId}`,
      editMedication: (filingId: number) => `/webforms/prior-auth-request/medication/${filingId}`,
      editDurableMedicalEquipment: (filingId: number) =>
        `/webforms/prior-auth-request/durable-medical-equipment/${filingId}`,
      editNonMtgOver1000: (filingId: number) =>
        `/webforms/prior-auth-request/non-mtg-over-1000/${filingId}`,
      editNonMtgUnder1000: (filingId: number) =>
        `/webforms/prior-auth-request/non-mtg-under-1000/${filingId}`,
      editSpecialServices: (filingId: number) =>
        `/webforms/prior-auth-request/special-services/${filingId}`,
      editVariance: (filingId: number) => `/webforms/prior-auth-request/variance/${filingId}`,
      submission: () => '/webforms/prior-auth-request/submission',
      getUrlByParType: (parRequestType: PriorAuthorizationRequestType, filingId: number) => {
        switch (parRequestType) {
          case PriorAuthorizationRequestType.Confirmation:
            return AppUrls.filings.priorAuthRequest.editConfirmation(filingId);
          case PriorAuthorizationRequestType.Medication:
            return AppUrls.filings.priorAuthRequest.editMedication(filingId);
          case PriorAuthorizationRequestType.DurableMedicalEquipment:
            return AppUrls.filings.priorAuthRequest.editDurableMedicalEquipment(filingId);
          case PriorAuthorizationRequestType.NonMtgBodyPartOver1000:
            return AppUrls.filings.priorAuthRequest.editNonMtgOver1000(filingId);
          case PriorAuthorizationRequestType.NonMtgBodyPartUnder1000:
            return AppUrls.filings.priorAuthRequest.editNonMtgUnder1000(filingId);
          case PriorAuthorizationRequestType.SpecialServices:
            return AppUrls.filings.priorAuthRequest.editSpecialServices(filingId);
          case PriorAuthorizationRequestType.Variance:
            return AppUrls.filings.priorAuthRequest.editVariance(filingId);
          default:
            return AppUrls.filings.priorAuthRequest.submission();
        }
      },
    },
    confirmation: (filingSubmissionId: number): string =>
      `webforms/confirmation/${filingSubmissionId}`,
  };

  static groups = {
    myGroups: (): string => 'my-groups',
  };

  static priorAuthorization = {
    details: (id: number): string => `/prior-authorization/${id}`,
    documentNotificationUserSelect: (id: number, jobId: string): string =>
      `/prior-authorization/${id}/notify-users/${jobId}`,
    respondToRequest: (priorAuthId: number): string =>
      `/webforms/prior-authorization/${priorAuthId}/response/create`,
    editDraftRequest: (priorAuthId: number, filingId: number): string =>
      `/webforms/prior-authorization/${priorAuthId}/response/${filingId}`,
    editRequest: (priorAuthId: number): string =>
      `/webforms/prior-authorization/${priorAuthId}/response/edit`,
    requestL2Review: (priorAuthId: number): string =>
      `/prior-authorization/${priorAuthId}/request-l2-review/create`,
    resumeL2Review: (priorAuthId: number, filingId: number): string =>
      `/prior-authorization/${priorAuthId}/request-l2-review/${filingId}`,
  };

  static medicalDirectorsOfficeReview = {
    details(id: number): string {
      return `/medical-directors-office-review/${id}`;
    },
    requestReview(id: number): string {
      return `/webforms/mdo-review/${id}`;
    },
    preview(id: number): string {
      return `/medical-directors-office-review/${id}/preview`;
    },
  };

  static userSecurity = {
    details(id: number): string {
      return `/user-security/details/${id}`;
    },
  };

  static administration = {
    groupManagement(entityTypeCode: string, entityId: number): string {
      return `/administration/group/${entityTypeCode}/${entityId}`;
    },
    queueManagement(id: number): string {
      return `/administration/queue/${id}`;
    },
    dashboard(): string {
      return '/administration/dashboard';
    },
  };

  static jobManagement = {
    jobs: () => '/administration/job-management',
    jobInfo: (jobName: string) => `/administration/job-management/job/${jobName}`,
    jobSteps: (jobId: number) => `/administration/job-management/job/${jobId}/job-steps`,
  };

  static home(): string {
    return '/';
  }

  static login(): string {
    return '/login';
  }

  static logout(): string {
    return '/logout';
  }

  static userProfile(): string {
    return '/user-profile';
  }

  static recentDownloads(): string {
    return '/recent-downloads';
  }

  static unauthorized(): string {
    return 'unauthorized';
  }

  static invalidDomain(): string {
    return 'invalid-domain';
  }

  static authCallback(): string {
    return 'auth-callback';
  }

  static notFound(): string {
    return '**';
  }

  static search(): string {
    return './search';
  }

  static mdoReviewAssignment(): string {
    return '/administration/mdo-review-assignment';
  }

  static adminDashboard(): string {
    return '/administration/dashboard';
  }

  static workflowAdminDashboard(): string {
    return '/work-request/admin';
  }

  public static urlsForSystemType(typeCode: SystemTypeCode) {
    switch (typeCode) {
      case EntityTypeCode.Employer:
        return this.entities.employer;
      case EntityTypeCode.HealthcareProvider:
        return this.entities.healthcareProvider;
      case EntityTypeCode.Claimant:
        return this.entities.claimant;
      case EntityTypeCode.Insurer:
        return this.entities.insurer;
      case EntityTypeCode.MedicalSupplier:
        return this.entities.medicalSupplier;
      case EntityTypeCode.ThirdPartyAdministrator:
        return this.entities.thirdPartyAdministrator;
      case ObjectTypeCode.Document:
        return this.document;
      case ObjectTypeCode.PriorAuthorization:
        return this.priorAuthorization;
      case ObjectTypeCode.MedicalBillingDispute:
        return this.medicalBillingDispute;
      case ObjectTypeCode.MdoReviewRequest:
        return this.medicalDirectorsOfficeReview;
      case EntityTypeCode.PharmacyBenefitManager:
        return this.entities.pharmacyBenefitManager;
      case EntityTypeCode.UtilizationReviewer:
        return this.entities.utilizationReviewer;
      default:
        throw new Error(`No URLs found for type code ${typeCode}.`);
    }
  }

  public static detailsForSystemType(entityId: number, objectTypeCode: SystemTypeCode) {
    return this.urlsForSystemType(objectTypeCode).details(entityId);
  }

  static detailsForTransaction(transactionId: number, transactionType: TransactionType) {
    switch (transactionType) {
      case TransactionType.Claim:
        return AppUrls.transactions.claim.details(transactionId);
      case null:
        return null;
      default:
        throw new Error(
          `AppUrls.detailsForTransaction does not support transactionType ${transactionType} yet.`,
        );
    }
  }
}
